import { supabase } from '../../libs/supabaseClient';
import { sync } from '../../libs/syncClient';
import {
  clearImpersonationState,
  getImpersonationState,
  recargarAppEn,
  saveImpersonationState,
} from '../../helpers/impersonation';
import type { Role } from '../../types/permissions';

/**
 * Panel del admin de SITIO (profiles.role = 'admin'): empresas de toda la
 * plataforma, sus usuarios, e impersonación.
 *
 * Seguridad: las RPC `admin_app_*` son SECURITY DEFINER con el gate
 * `es_admin_app()` adentro (devuelven vacío a cualquier otro usuario) y la
 * sesión de impersonación la emite la Edge Function `admin-impersonate`, que
 * revalida el rol server-side y audita en `admin_impersonaciones`.
 * Migración: 20260926120000_admin_app_empresas_impersonacion.sql.
 */

export interface AdminEmpresa {
  empresa_id: string;
  nombre: string;
  renspa: string | null;
  creada_el: string;
  eliminada: boolean;
  cantidad_usuarios_activos: number;
  cantidad_usuarios_total: number;
  cantidad_animales: number;
}

export type MembershipStatus = 'pending' | 'active' | 'inactive' | 'suspended';

export interface AdminEmpresaUsuario {
  user_id: string;
  email: string | null;
  nombre: string | null;
  apellido: string | null;
  role: Role;
  status: MembershipStatus;
  invited_at: string | null;
  accepted_at: string | null;
  es_admin_sitio: boolean;
  empresa_activa: boolean;
}

/** Ruta a la que vuelve el admin al terminar la impersonación. */
export const ADMIN_EMPRESAS_PATH = '/devtools?tab=empresas';

export class AdminAppService {
  static async listarEmpresas(): Promise<AdminEmpresa[]> {
    const { data, error } = await supabase.rpc('admin_app_listar_empresas');
    if (error) throw new Error(error.message);
    return ((data ?? []) as AdminEmpresa[]).map((e) => ({
      ...e,
      cantidad_usuarios_activos: Number(e.cantidad_usuarios_activos),
      cantidad_usuarios_total: Number(e.cantidad_usuarios_total),
      cantidad_animales: Number(e.cantidad_animales),
    }));
  }

  static async listarUsuariosEmpresa(empresaId: string): Promise<AdminEmpresaUsuario[]> {
    const { data, error } = await supabase.rpc('admin_app_usuarios_empresa', {
      p_empresa_id: empresaId,
    });
    if (error) throw new Error(error.message);
    return (data ?? []) as AdminEmpresaUsuario[];
  }

  /**
   * Empuja los cambios pendientes de la sesión actual antes de cambiar de
   * usuario: si quedaran en la cola, se enviarían con la identidad del otro
   * usuario (y la RLS los rechazaría o, peor, los atribuiría mal).
   */
  private static async flushPendientes(): Promise<void> {
    const pendientes = (await sync.getPendingCount?.()) ?? 0;
    if (pendientes === 0) return;
    if (!sync.isOnline?.()) {
      throw new Error(
        `Hay ${pendientes} cambio(s) sin sincronizar. Conectate a internet antes de cambiar de usuario.`,
      );
    }
    await sync.syncToRemote?.();
    const restantes = (await sync.getPendingCount?.()) ?? 0;
    if (restantes > 0) {
      throw new Error(
        `No se pudieron sincronizar ${restantes} cambio(s). Reintentá antes de cambiar de usuario.`,
      );
    }
  }

  /**
   * Inicia sesión como `usuario` dentro de `empresa`. Guarda la sesión del
   * admin para volver y recarga la app completa (caché local, sync y
   * permisos arrancan limpios con la identidad nueva).
   */
  static async impersonar(
    usuario: { user_id: string; email: string | null; label: string },
    empresa: { id: string; nombre: string },
  ): Promise<void> {
    if (getImpersonationState()) {
      throw new Error('Ya estás impersonando a un usuario. Volvé a tu cuenta primero.');
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new Error('Necesitás conexión a internet para impersonar a un usuario.');
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sesión expirada. Volvé a iniciar sesión.');

    await AdminAppService.flushPendientes();

    const { data, error } = await supabase.functions.invoke('admin-impersonate', {
      body: { targetUserId: usuario.user_id, empresaId: empresa.id },
    });
    if (error || !data?.token_hash) {
      let mensaje = data?.error ?? error?.message ?? 'No se pudo impersonar al usuario';
      // FunctionsHttpError: el cuerpo con el mensaje viene en error.context.
      try {
        const body = await (error as { context?: Response })?.context?.json?.();
        if (body?.error) mensaje = body.error;
      } catch {
        /* sin cuerpo legible */
      }
      throw new Error(mensaje);
    }

    // Guardar la sesión del admin ANTES de reemplazarla.
    saveImpersonationState({
      adminSession: {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      },
      adminUserId: session.user.id,
      adminEmail: session.user.email ?? null,
      target: { userId: usuario.user_id, email: usuario.email, label: usuario.label },
      empresa,
      startedAt: new Date().toISOString(),
    });

    const { error: otpError } = await supabase.auth.verifyOtp({
      token_hash: data.token_hash,
      type: 'magiclink',
    });
    if (otpError) {
      // La sesión del admin sigue intacta (verifyOtp falló antes de pisarla).
      clearImpersonationState();
      throw new Error(`No se pudo iniciar la sesión del usuario: ${otpError.message}`);
    }

    recargarAppEn('/dashboard');
  }

  /**
   * Termina la impersonación: cierra (y revoca) la sesión del usuario y
   * restaura la del admin. Si la sesión del admin ya no es válida, lo manda
   * a loguearse de nuevo.
   */
  static async terminarImpersonacion(): Promise<void> {
    const state = getImpersonationState();
    if (!state) return;

    await AdminAppService.flushPendientes();

    // Primero se restaura la sesión del admin y DESPUÉS se revoca la
    // impersonada: un signOut() previo emite SIGNED_OUT y la app redirige a
    // /login, y ese navigate pisa la recarga de abajo.
    const { data: { session: impersonada } } = await supabase.auth.getSession();

    const { error } = await supabase.auth.setSession(state.adminSession);
    clearImpersonationState();
    if (error) {
      console.warn('[AdminAppService] No se pudo restaurar la sesión del admin:', error.message);
      await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
      recargarAppEn('/login');
      return;
    }

    // Revoca el refresh token de la sesión impersonada con su propio JWT
    // (scope local = sólo esa sesión; las del usuario en sus dispositivos no
    // se tocan). No requiere service_role: /logout sólo valida el JWT.
    if (impersonada?.access_token) {
      await supabase.auth.admin
        .signOut(impersonada.access_token, 'local')
        .catch((e) => console.warn('[AdminAppService] No se pudo revocar la sesión impersonada:', e));
    }

    recargarAppEn(ADMIN_EMPRESAS_PATH);
  }
}
